import { parseAndEmbed, progressLabel, type ParseProgress } from "../api/epflServer";
import { fileToBase64, keepsOriginal } from "../api/docxEdits";
import * as documents from "../db/documents";
import { app, type UploadItem } from "./app.svelte";
import { tr } from "../i18n/i18n.svelte";

/** One AbortController per upload still queued or parsing, so a row's
 *  « Annuler » can stop it (the server drops the parse when the stream closes). */
const controllers = new Map<string, AbortController>();

/** Cancel a queued or in-flight upload. Its row disappears; nothing is saved. */
export function cancelUpload(id: string): void {
  controllers.get(id)?.abort();
  controllers.delete(id);
  app.uploads = app.uploads.filter((u) => u.id !== id);
}

type UploadEvent =
  | ParseProgress
  | { type: "progress"; step: "embed"; done: number; total: number };

/** Map a parse/embedding event to the structured progress of the row. Shares
 *  of the bar: reading the text up to 35 % (80 % when OCR runs page by page),
 *  embedding the last 20 %. Rough by design — it only has to keep moving. */
function progressPatch(evt: UploadEvent, item: UploadItem): Partial<UploadItem> {
  if (evt.type === "phase") {
    switch (evt.phase) {
      case "received":
        return { fraction: 0.05, pagesTotal: evt.total_pages || undefined };
      case "native_extracting":
        return { fraction: 0.1 };
      case "native_extracted":
        return {
          fraction: 0.35,
          pagesDone: evt.n_pages,
          pagesTotal: item.pagesTotal ?? evt.n_pages,
        };
      case "ocr_started":
        return { fraction: 0.35, ocr: true, pagesDone: 0, pagesTotal: evt.total_pages };
      case "chunking":
        return { fraction: 0.8 };
    }
  }
  if (evt.type === "progress" && evt.step === "ocr") {
    return {
      fraction: 0.35 + (0.45 * evt.page) / Math.max(evt.total, 1),
      pagesDone: evt.page,
      pagesTotal: evt.total,
    };
  }
  if (evt.type === "progress" && evt.step === "embed") {
    return { fraction: 0.8 + (0.2 * evt.done) / Math.max(evt.total, 1) };
  }
  return {};
}

export interface UploadInput {
  file: File;
  /** Folder path the resulting document should land in, or null for root. */
  folder: string | null;
}

/** Upload files serially to avoid GPU/CPU contention on the server.
 *  Individual progress reported via app.uploads (status + detail per file).
 *  Each input carries its own target folder (used when a whole directory is
 *  dropped and files spread across sub-folders).
 */
export async function uploadFiles(inputs: UploadInput[], inChat = false): Promise<void> {
  if (!app.config) return;
  const cfg = app.config;

  // Drop terminal rows from a previous batch (kept "error" rows from last time),
  // but leave anything still in flight.
  app.uploads = app.uploads.filter(
    (u) => u.status === "parsing" || u.status === "queued",
  );

  // Make sure every referenced folder exists (and is persisted) up front.
  for (const folder of new Set(inputs.map((i) => i.folder))) {
    if (folder) await app.createFolder(folder);
  }

  const items: UploadItem[] = inputs.map((i) => ({
    id: crypto.randomUUID(),
    filename: i.file.name,
    size: i.file.size,
    status: "queued",
    ...(inChat ? { inChat } : {}),
  }));
  app.uploads = [...app.uploads, ...items];
  for (const item of items) controllers.set(item.id, new AbortController());

  for (let i = 0; i < inputs.length; i++) {
    const { file, folder } = inputs[i];
    const item = items[i];
    const controller = controllers.get(item.id);
    // Cancelled while still queued.
    if (!controller || controller.signal.aborted) continue;
    updateItem(item.id, {
      status: "parsing",
      detail: tr({ fr: "en cours…", en: "in progress…" }),
      fraction: 0,
      startedAt: Date.now(),
    });

    try {
      const doc = await parseAndEmbed(
        file,
        cfg,
        (evt) => {
          const current = app.uploads.find((u) => u.id === item.id);
          const patch = current ? progressPatch(evt, current) : {};
          if (evt.type === "progress" && evt.step === "embed") {
            updateItem(item.id, {
              ...patch,
              detail: `embedding ${evt.done}/${evt.total}…`,
            });
            return;
          }
          const label = progressLabel(evt);
          updateItem(item.id, label ? { ...patch, detail: label } : patch);
        },
        controller.signal,
      );
      if (controller.signal.aborted) continue;
      doc.folder = folder;
      if (keepsOriginal(file, doc.ocrUsed)) doc.original = await fileToBase64(file);
      await documents.putDocument(doc);
      updateItem(item.id, { status: "done", detail: undefined, docId: doc.id });
      app.activeDocIds = [...app.activeDocIds, doc.id];
      await app.refreshDocs();
    } catch (e) {
      // « Annuler » : the row is already gone, this is not an error.
      if (controller.signal.aborted) continue;
      const msg = e instanceof Error ? e.message : String(e);
      updateItem(item.id, { status: "error", error: msg, detail: undefined });
      app.reportError(e);
    } finally {
      controllers.delete(item.id);
    }
  }

  // Clear successful rows after a moment, but KEEP error rows on screen so the
  // user can see which files failed and why (they're cleared at the next upload).
  setTimeout(() => {
    app.uploads = app.uploads.filter((u) => u.status !== "done");
  }, 4000);
}

function updateItem(id: string, patch: Partial<UploadItem>): void {
  app.uploads = app.uploads.map((u) => (u.id === id ? { ...u, ...patch } : u));
}
